/**
 * carModel — AUTOMÓVEIS low-poly reconhecíveis. Cada "peça" vira um InstancedMesh
 * no Fleet; detalhes do MESMO material são mesclados (mergeGeometries) p/ poucas
 * draw calls. Só a pintura do corpo varia por jogador (instanceColor `paint`).
 *
 * MODELOS (variant 'model' → a peça só aparece p/ quem tem aquele modelo):
 *   • defender — Land Rover Defender 90: caixote, BICOLOR (teto prata), faróis
 *                redondos, estepe na traseira.
 *   • hilux    — Toyota Hilux cabine-dupla: PICKUP com caçamba aberta + santantônio
 *                (sports bar), frente moderna (grade grande + faróis angulares),
 *                estribos laterais; teto na cor do corpo.
 *
 * Eixos: +Z = frente (faróis/grade), -Z = traseira. Largura em X.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { beveledBox } from "../geometry/beveledBox.js";
import { finishWheelWells } from "./wheelWells.js";
import { makeCarGlass } from "./carGlass.js";
import { setAvatarDetail, sphSeg, cylSeg, curveSeg } from "./lod.js";

const HALF_PI = Math.PI / 2;

const CAR_COLORS = ["#9caf88", "#e6584d", "#f2c14e", "#5b8def", "#2fce5e", "#e8ecea", "#c879e6", "#39c6c6", "#ef8c3b", "#384b41", "#b8c2bf"];
const MODELS = ["defender", "hilux", "porsche", "rav4", "polestar", "fusca"];
const pick = (a) => a[(Math.random() * a.length) | 0];

/** Aparência aleatória do carro. `p.color` vira a pintura do corpo. */
export function randomCarAppearance(p) {
    const paint = (p && p.color) || pick(CAR_COLORS);
    // `cabin` mantido por compatibilidade (não vira cor: o teto é material fixo).
    return { model: pick(MODELS), wheels: "alloy", colors: { paint, cabin: paint } };
}

/**
 * Garante um `model` VÁLIDO. O backend (ao vivo) pode mandar modelos ANTIGOS
 * (ex.: 'suv'/'sedan') que não casam com nenhuma peça → o carro renderiza VAZIO
 * (invisível). Aqui caímos num modelo válido mantendo as cores do jogador.
 */
export function normalizeCarAppearance(app, p) {
    if (!app) return randomCarAppearance(p);
    if (MODELS.includes(app.model)) return app;
    return { ...app, model: pick(MODELS) };
}

// caixas com arestas CRISP (cantos quase retos, mais REAIS). O arredondamento alto
// (raio 0.22) deixou os carros "desformatados"/inflados → fillet pequeno só p/ tirar
// o serrilhado das quinas.
const rad = (w, h, d) => Math.min(0.1, Math.min(w, h, d) * 0.38);
// Normaliza p/ mergeGeometries() poder misturar caixa + cilindro: mesmos atributos
// (remove UV — nenhum material usa textura) E mesma indexação (tudo NÃO-indexado;
// BeveledBox e Cylinder diferem nisso → o merge recusaria).
const noUV = (g) => {
    const ng = g.index ? g.toNonIndexed() : g;
    ng.deleteAttribute("uv");
    return ng;
};
const box = (w, h, d) => { const r = rad(w, h, d); return noUV(beveledBox(w, h, d, r)); };
const tbox = (w, h, d, x, y, z) => box(w, h, d).translate(x, y, z);
// caixa CRISP (cantos quase retos) — modelos CAIXOTE (Defender): evita o ar "inflado"
const cbox = (w, h, d) => { const r = Math.min(0.1, Math.min(w, h, d) * 0.4); return noUV(beveledBox(w, h, d, r)); };
const tcbox = (w, h, d, x, y, z) => cbox(w, h, d).translate(x, y, z);
// caixa um POUCO mais arredondada (esportivo/crossover: 911/RAV4) — fillet levemente
// maior que box, mas ainda CRISP (não bojudo/cápsula como antes, raio 0.28).
const rbox = (w, h, d) => { const r = Math.min(0.13, Math.min(w, h, d) * 0.36); return noUV(beveledBox(w, h, d, r)); };
const trbox = (w, h, d, x, y, z) => rbox(w, h, d).translate(x, y, z);
// caixa de CORPO ALTO (Hilux/RAV4) — antes tinha raio GRANDE (0.36) que INFLAVA/
// "desformatava" os corpos; agora CRISP como as demais (arestas reais).
const hbox = (w, h, d) => { const r = Math.min(0.12, Math.min(w, h, d) * 0.34); return noUV(beveledBox(w, h, d, r)); };
const thbox = (w, h, d, x, y, z) => hbox(w, h, d).translate(x, y, z);
// caixa girada em Y (faróis varridos) e posicionada
const abox = (w, h, d, ry, x, y, z) => box(w, h, d).rotateY(ry).translate(x, y, z);
const cyl = (r, depth, seg) => noUV(new THREE.CylinderGeometry(r, r, depth, cylSeg(seg)));
// disco "de roda": cilindro deitado (eixo em X, p/ rolar); centro em y=r (toca o chão)
const wheelDisc = (r, depth, seg, x, z) => cyl(r, depth, seg).rotateZ(HALF_PI).translate(x, r, z);
// disco no EIXO (y explícito) — p/ aro/cubo CONCÊNTRICOS com o pneu. wheelDisc centra
// em y=r (bom p/ o pneu, que toca o chão), mas usá-lo no aro de raio menor o deixava
// MAIS BAIXO que o pneu (desalinhado, "tocando o chão"). Aqui todos usam o MESMO eixo.
const axleDisc = (r, depth, seg, x, y, z) => cyl(r, depth, seg).rotateZ(HALF_PI).translate(x, y, z);
// Open five-spoke alloys: the existing dark tire shows through the openings.
// All surfaces stay in the rim's material/instance; steering retains its pivot.
function alloyRim(r, depth, seg) {
    const n = cylSeg(seg);
    const pieces = [
        noUV(new THREE.CylinderGeometry(r, r, depth, n, 1, true)),
        noUV(new THREE.RingGeometry(r * 0.78, r, n)).rotateX(-HALF_PI).translate(0, depth / 2, 0),
        noUV(new THREE.RingGeometry(r * 0.78, r, n)).rotateX(HALF_PI).translate(0, -depth / 2, 0),
    ];
    for (let i = 0; i < 5; i++) {
        pieces.push(noUV(new THREE.BoxGeometry(r * 0.22, depth, r * 0.84))
            .translate(0, 0, r * 0.45).rotateY(i * Math.PI * 2 / 5));
    }
    return mergeGeometries(pieces);
}
const alloyAxle = (r, depth, seg, x, y, z) => alloyRim(r, depth, seg).rotateZ(HALF_PI).translate(x, y, z);
// Both rear wheels share the same X axle. Keep each material merged in one
// instance, but move its geometry into axle-local space so it can roll in place.
const rearAxlePart = (geo, mat, y, z, extra = {}) => ({
    geo: geo.translate(0, -y, -z), mat, pos: [0, y, z], anim: "wheel", ...extra,
});
// RODA DIANTEIRA esterçável: cada camada (pneu/aro/cubo) é um cilindro deitado (rola em
// X) CENTRADO na ORIGEM (o eixo) e posicionado por `pos=[x,AY,z]` → o Fleet gira em Y
// (esterço, no ângulo da curva) NO CENTRO da roda. Visual idêntico ao das merged com
// esterço 0. `layers`: [{ r, depth, seg, mat, color? }] (pneu → aro → cubo).
const steerWheelParts = (x, z, AY, layers) =>
    layers.map((l) => ({
        geo: (l.alloy ? alloyRim(l.r, l.depth, l.seg) : cyl(l.r, l.depth, l.seg)).rotateZ(HALF_PI),
        mat: l.mat,
        pos: [x, AY, z],
        anim: "wheelF",
        wheelRadius: layers[0].r,
        wheelTire: l === layers[0],
        ...(l.color ? { color: l.color } : {}),
    }));
// disco vertical encarando ±Z (estepe / aros de farol / emblema)
const zDisc = (r, depth, seg, x, y, z) => cyl(r, depth, seg).rotateX(HALF_PI).translate(x, y, z);
// ESTUFA com para-brisas INCLINADOS (não "caixote"): perfil lateral trapezoidal —
// base (cintura) z [zR..zF]; topo (teto) RECUADO na frente (rakeF) e AVANÇADO atrás
// (rakeR), extrudado pela largura `w`. Janelas laterais ficam verticais.
const rakedGlass = (w, zF, zR, yB, yT, rakeF, rakeR) => {
    const s = new THREE.Shape();
    s.moveTo(zR, yB);
    s.lineTo(zF, yB);
    s.lineTo(zF - rakeF, yT); // topo do PARA-BRISA recua → inclina
    s.lineTo(zR + rakeR, yT); // topo do VIDRO TRASEIRO avança → inclina
    s.closePath();
    const g = new THREE.ExtrudeGeometry(s, { depth: w, bevelEnabled: false });
    g.translate(0, 0, -w / 2); // centraliza a largura
    g.rotateY(-HALF_PI); // perfil → mundo (shapeX→Z, shapeY→Y, extrude→X)
    return noUV(g);
};

/** Marca todas as peças de um modelo (variant 'model'). */
const tagModel = (model, arr) => arr.map((p) => ({ ...p, variant: "model", option: model }));

/**
 * MOLDURA de caixa de roda (band) ABERTA embaixo — contorno externo quadrado com
 * cantos ARREDONDADOS + borda interna redonda seguindo a roda (arco por cima).
 * Extrudada em X (plano YZ; a roda rola em Z). Reutilizável (Defender, Hilux, …).
 * cfg: AY=altura do eixo, W=meia-largura, yB=base (linha do rocker), yT=topo,
 *      rc=raio dos cantos sup., rIn=raio da abertura (>raio do pneu), depth=espessura X.
 */
function archBand(x, z, { AY, W, yB, yT, rc, rIn, depth }) {
    const dy = yB - AY;
    const xIn = Math.sqrt(rIn * rIn - dy * dy);
    const aRear = Math.PI - Math.asin(dy / rIn);
    const aFront = Math.asin(dy / rIn);
    const s = new THREE.Shape();
    s.moveTo(W, yB);
    s.lineTo(W, yT - rc);
    s.quadraticCurveTo(W, yT, W - rc, yT); // canto sup. dianteiro arredondado
    s.lineTo(-(W - rc), yT);
    s.quadraticCurveTo(-W, yT, -W, yT - rc); // canto sup. traseiro arredondado
    s.lineTo(-W, yB);
    s.lineTo(-xIn, yB);
    s.absarc(0, AY, rIn, aRear, aFront, true); // abertura redonda (segue a roda), por cima
    s.closePath();
    const geo = new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: false, curveSegments: curveSeg(16) });
    geo.translate(0, 0, -depth / 2);
    geo.rotateY(-HALF_PI);
    return noUV(geo.translate(x, 0, z));
}

/**
 * Arco de roda REDONDO (banda concêntrica com a roda) — contorno interno E externo
 * seguindo o círculo da roda (não quadrado), aberto embaixo. Para carros sleek (Polestar)
 * onde o para-lama acompanha o pneu. cfg: AY=altura do eixo, rIn=raio da abertura,
 * rOut=raio externo (rOut-rIn = espessura), yB=base (rocker), depth=espessura em X.
 */
function roundArch(x, z, { AY, rIn, rOut, yB, depth }) {
    const dy = yB - AY;
    const xIn = Math.sqrt(rIn * rIn - dy * dy);
    const xOut = Math.sqrt(rOut * rOut - dy * dy);
    const aFrontIn = Math.asin(dy / rIn),
        aRearIn = Math.PI - aFrontIn;
    const aFrontOut = Math.asin(dy / rOut),
        aRearOut = Math.PI - aFrontOut;
    const s = new THREE.Shape();
    s.moveTo(xOut, yB);
    s.absarc(0, AY, rOut, aFrontOut, aRearOut, false); // arco EXTERNO por cima (segue a roda)
    s.lineTo(-xIn, yB);
    s.absarc(0, AY, rIn, aRearIn, aFrontIn, true); // arco INTERNO de volta (segue a roda)
    s.closePath();
    const geo = new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: false, curveSegments: curveSeg(28) });
    geo.translate(0, 0, -depth / 2);
    geo.rotateY(-HALF_PI);
    return noUV(geo.translate(x, 0, z));
}

/**
 * Peças de TODOS os modelos de carro (procedurais), prontas p/ o Fleet (InstancedMesh).
 * @param {'game'|'studio'} detail tesselação (ver lod.js). 'game' = câmera aérea da
 *        cidade (padrão); 'studio' = close-up do /models e do retrato de conquista.
 *        A lista de peças, posições e cores é IDÊNTICA nos dois — só a densidade
 *        de vértices muda.
 */
export function buildCarParts(detail) {
    const prevDetail = setAvatarDetail(detail);
    try {
        return buildCarPartsInner();
    } finally {
        setAvatarDetail(prevDetail);
    }
}

function buildCarPartsInner() {
    // ---- materiais compartilhados ----
    const mats = {
        // Restrained highlights, with a shared soft environment for the glass.
        // No transparency, extra passes or per-car material buckets.
        paintMat: new THREE.MeshPhongMaterial({ color: 0xffffff, specular: "#555b60", shininess: 52 }),
        glass: makeCarGlass(),
        clad: new THREE.MeshLambertMaterial({ color: "#232826" }), // escuro (cladding/para-choques/grade/sports bar)
        chrome: new THREE.MeshPhongMaterial({ color: "#bac5ca", specular: "#bbc5ce", shininess: 85 }), // friso/skid/emblema
        rimMat: new THREE.MeshPhongMaterial({ color: "#9eacb4", specular: "#8a96a0", shininess: 64 }),
        tire: new THREE.MeshLambertMaterial({ color: "#0a0d0c" }),
        // Small moving lenses must stay below the bloom threshold (0.86 in
        // linear space). Otherwise reduced-resolution extraction alternately
        // catches/misses them, creating flashing halos. Basic keeps the lens
        // lit at night; tone mapping still gives it a bright warm appearance.
        headMat: new THREE.MeshBasicMaterial({ name: "car-headlight", color: new THREE.Color("#fff6df").multiplyScalar(0.72) }),
        tailMat: new THREE.MeshBasicMaterial({ color: "#ff3b3b" }), // lanternas
    };
    // builder de PRIMITIVAS por modelo (todos os carros são procedurais)
    const primitive = {
        defender: () => buildDefender(mats),
        hilux: () => buildHilux(mats),
        porsche: () => buildPorsche(mats),
        rav4: () => buildRav4(mats),
        polestar: () => buildPolestar(mats),
        fusca: () => buildFusca(mats),
    };
    const out = [];
    for (const model of MODELS) {
        out.push(...tagModel(model, finishWheelWells(primitive[model](), mats.clad)));
    }
    return out;
}

/* ===================== LAND ROVER DEFENDER 110 (Explorer Pack) ===================== */
// Referência: docs/cars/defender (Mk2 110 Explorer Pack 2020). 5 portas: ENTRE-EIXOS
// LONGO, overhangs curtos, corpo ALTO e caixote arredondado. Marcas: BAGAGEIRO preto
// no teto, ARCOS DE RODA pretos quadrados (ponto mais largo), ESTRIBO lateral, ESTEPE
// na traseira, FARÓIS REDONDOS, capô com painel central. Eixos: +Z = frente.
function buildDefender({ paintMat, glass, clad, chrome, rimMat, tire, headMat, tailMat }) {
    const parts = [];
    const FA = 1.5; // eixo dianteiro (z)
    const RA = -1.5; // eixo traseiro (z) → entre-eixos = 3.0 (longo, 110)

    // ---- PINTURA (cor do jogador): corpo + capô + teto + coluna traseira ----
    parts.push({
        geo: mergeGeometries([
            tcbox(2.0, 1.08, 4.7, 0, 0.94, -0.02), // corpo CAIXOTE alto (y 0.40..1.48) — LONGO (balanços contêm os arcos)
            tcbox(1.88, 0.12, 1.66, 0, 1.46, 1.4), // capô plano (estendido p/ frente: z 0.57..2.23)
            tcbox(1.5, 0.05, 1.48, 0, 1.51, 1.44), // leve elevação central do capô
            tcbox(1.86, 0.16, 3.73, 0, 2.27, -0.475), // TETO (cor do corpo) — estendido até a traseira (z≈-2.34) p/ cobrir o balanço + rack
            tcbox(1.98, 0.76, 1.04, 0, 1.81, -1.85), // coluna/painel traseiro (cargo) — do vidro traseiro até a traseira
        ]),
        mat: paintMat,
        pos: [0, 0, 0],
        color: "paint",
    });

    // ---- VIDROS: estufa (para-brisa inclinado + laterais + traseiro) ----
    parts.push({ geo: rakedGlass(1.82, 1.16, -1.4, 1.5, 2.18, 0.2, 0.14), mat: glass, pos: [0, 0, 0] });

    // ---- ESCURO (clad): header preto, para-choques, grade, faróis, gills ----
    parts.push({
        geo: mergeGeometries([
            tcbox(1.86, 0.16, 0.5, 0, 2.26, 1.04), // header preto (sobre o para-brisa)
            tcbox(2.06, 0.34, 0.34, 0, 0.5, 2.42), // para-choque dianteiro (baixo)
            tcbox(2.02, 0.34, 0.34, 0, 0.5, -2.44), // para-choque traseiro (baixo)
            tcbox(0.88, 0.44, 0.12, 0, 1.0, 2.38), // grade (central, estreita — abre espaço p/ os faróis)
            tcbox(0.34, 0.05, 0.2, -0.42, 1.5, 1.0), // vent do capô E
            tcbox(0.34, 0.05, 0.2, 0.42, 1.5, 1.0), // vent do capô D
            zDisc(0.21, 0.1, 16, -0.62, 1.24, 2.35), // moldura do farol E (p/ DENTRO, longe do arco)
            zDisc(0.21, 0.1, 16, 0.62, 1.24, 2.35), // moldura do farol D
            tcbox(0.07, 0.32, 0.42, 1.0, 1.12, 0.46), // gill lateral D (atrás da roda diant.)
            tcbox(0.07, 0.32, 0.42, -1.0, 1.12, 0.46), // gill lateral E
            // colunas B/C (dividem a estufa em janelas) — pretas, sobre o vidro
            tcbox(0.06, 0.66, 0.13, 0.92, 1.83, 0.04),
            tcbox(0.06, 0.66, 0.13, -0.92, 1.83, 0.04), // pilar B
            tcbox(0.06, 0.62, 0.13, 0.92, 1.8, -0.92),
            tcbox(0.06, 0.62, 0.13, -0.92, 1.8, -0.92), // pilar C
        ]),
        mat: clad,
        pos: [0, 0, 0],
    });

    // ---- BAGAGEIRO (expedition roof rack) — preto, plano, ripado ----
    {
        const rg = [];
        const z0 = 0.86,
            z1 = -2.18,
            ry = 2.46,
            L = z0 - z1;
        const n = 9;
        for (let i = 0; i <= n; i++) rg.push(tcbox(1.62, 0.05, 0.07, 0, ry, z0 + (z1 - z0) * (i / n))); // ripas transversais
        rg.push(tcbox(0.08, 0.06, L, 0.76, ry, (z0 + z1) / 2)); // longarina D
        rg.push(tcbox(0.08, 0.06, L, -0.76, ry, (z0 + z1) / 2)); // longarina E
        rg.push(tcbox(1.72, 0.15, 0.08, 0, ry + 0.06, z0)); // lip frontal (perímetro elevado)
        rg.push(tcbox(1.72, 0.15, 0.08, 0, ry + 0.06, z1)); // lip traseiro
        rg.push(tcbox(0.08, 0.15, L + 0.08, 0.82, ry + 0.06, (z0 + z1) / 2)); // lip lateral D
        rg.push(tcbox(0.08, 0.15, L + 0.08, -0.82, ry + 0.06, (z0 + z1) / 2)); // lip lateral E
        for (const lz of [z0 - 0.18, (z0 + z1) / 2, z1 + 0.18]) {
            // pernas (mounts) p/ o teto
            rg.push(tcbox(0.1, 0.16, 0.1, 0.72, 2.38, lz));
            rg.push(tcbox(0.1, 0.16, 0.1, -0.72, 2.38, lz));
        }
        parts.push({ geo: mergeGeometries(rg), mat: clad, pos: [0, 0, 0] });
    }

    // ---- FARÓIS REDONDOS (lentes claras, intensidade estável) ----
    parts.push({ geo: mergeGeometries([zDisc(0.15, 0.12, 16, -0.62, 1.24, 2.41), zDisc(0.15, 0.12, 16, 0.62, 1.24, 2.41)]), mat: headMat, pos: [0, 0, 0] });

    // ---- LANTERNAS verticais (traseira) ----
    parts.push({ geo: mergeGeometries([tcbox(0.17, 0.5, 0.08, -0.78, 1.16, -2.44), tcbox(0.17, 0.5, 0.08, 0.78, 1.16, -2.44)]), mat: tailMat, pos: [0, 0, 0] });

    // ---- CROMADOS: estribos laterais + skid plates (frente/trás) + friso da grade ----
    parts.push({
        geo: mergeGeometries([
            tcbox(0.16, 0.1, 2.04, 1.06, 0.46, 0.0), // estribo D
            tcbox(0.16, 0.1, 2.04, -1.06, 0.46, 0.0), // estribo E
            tcbox(1.06, 0.12, 0.3, 0, 0.4, 2.56), // skid dianteiro (prata, quebra o preto)
            tcbox(1.06, 0.12, 0.3, 0, 0.4, -2.58), // skid traseiro
            tcbox(0.84, 0.07, 0.1, 0, 1.26, 2.4), // friso cromado no topo da grade
        ]),
        mat: chrome,
        pos: [0, 0, 0],
    });

    // ---- RODAS (4, GRANDES) + ESTEPE — pneu + aro + cubo CONCÊNTRICOS no eixo (AY) ----
    // AY = raio do pneu → o PNEU toca o chão; o aro (alloy claro) e o cubo (escuro)
    // ficam no mesmo centro e PROEMINENTES (sobressaem o pneu) → leem como alloy, não
    // um disco escuro. Projetam além da caixa de roda → separação clara.
    const slots = [
        [-0.96, FA],
        [0.96, FA],
        [-0.96, RA],
        [0.96, RA],
    ]; // rodas recuadas (mais pra dentro)
    const front = [[-0.96, FA], [0.96, FA]];
    const rear = [[-0.96, RA], [0.96, RA]];
    const spare = [-0.24, 1.16, -2.48];
    const AY = 0.6;
    // Rear axle rolls; the spare stays attached to the body.
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => wheelDisc(0.6, 0.46, 20, x, z))), tire, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => alloyAxle(0.42, 0.54, 16, x, AY, z))), rimMat, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => axleDisc(0.13, 0.58, 10, x, AY, z))), clad, AY, RA));
    parts.push({ geo: zDisc(0.6, 0.36, 20, ...spare), mat: tire, pos: [0, 0, 0] });
    parts.push({ geo: zDisc(0.42, 0.48, 16, ...spare), mat: rimMat, pos: [0, 0, 0] });
    parts.push({ geo: zDisc(0.13, 0.52, 10, ...spare), mat: clad, pos: [0, 0, 0] });
    for (const [x, z] of front) parts.push(...steerWheelParts(x, z, AY, [
        { r: 0.6, depth: 0.46, seg: 20, mat: tire },
        { r: 0.42, depth: 0.54, seg: 16, mat: rimMat, alloy: true },
        { r: 0.13, depth: 0.58, seg: 10, mat: clad },
    ]));

    // ---- CAIXAS DE RODA: moldura band ABERTA embaixo (helper archBand compartilhado) ----
    {
        const panels = [],
            wells = [];
        for (const [x, z] of slots) {
            panels.push(archBand(x, z, { AY, W: 0.72, yB: 0.48, yT: 1.36, rc: 0.12, rIn: 0.64, depth: 0.32 }));
            wells.push(axleDisc(0.62, 0.12, 18, x * 0.93, AY, z)); // fundo escuro (well) — anel atrás da roda
        }
        parts.push({ geo: mergeGeometries(panels), mat: paintMat, pos: [0, 0, 0], color: "paint" }); // arcos na COR DA CARROCERIA
        parts.push({ geo: mergeGeometries(wells), mat: clad, pos: [0, 0, 0] });
    }

    return parts;
}

/* ===================== TOYOTA HILUX (cabine dupla) ===================== */
function buildHilux({ paintMat, glass, clad, chrome, rimMat, tire, headMat, tailMat }) {
    const parts = [];
    // Toyota Hilux Mk8f Double Cab SR5 2024 — ref docs/cars/hilux. PICKUP cabine-dupla,
    // postura ALTA: grade trapezoidal GRANDE (moldura clara + logo), faróis LED SLIM
    // swept + DRL, PODS de neblina, ARCOS de roda pretos, ROLL BAR na caçamba, steps,
    // mud flaps. Eixos: +Z frente. Rodas: front z=1.5, rear z=-1.9 (roda central na
    // caçamba; caçamba ENCOSTADA na cabine → visual de camionete, não caminhãozinho).
    const FA = 1.5,
        RA = -1.9,
        AY = 0.52;

    // ---- PINTURA: frente + cabine dupla + capô + teto + caçamba ----
    parts.push({
        geo: mergeGeometries([
            thbox(2.0, 1.02, 3.3, 0, 0.94, 0.65), // frente + cabine dupla (cintura y≈1.45)
            tbox(1.84, 0.12, 1.1, 0, 1.46, 1.66), // capô
            tbox(1.36, 0.05, 0.72, 0, 1.53, 1.58), // leve bulge central do capô
            tbox(1.9, 0.16, 1.56, 0, 2.3, -0.11), // teto (casa c/ o vidro traseiro vertical)
            thbox(1.96, 0.52, 1.86, 0, 0.68, -1.9), // assoalho/bloco baixo (frente z≈-0.97: encosta na cabine)
            tbox(0.15, 0.52, 1.86, 0.93, 1.19, -1.9), // mureta lateral D (segue a cintura da cabine, sem vão)
            tbox(0.15, 0.52, 1.86, -0.93, 1.19, -1.9), // mureta lateral E
            tbox(1.72, 0.52, 0.14, 0, 1.19, -1.0), // parede frontal (headboard) encostada na cabine
            tbox(1.7, 0.56, 0.16, 0, 1.13, -2.83), // tampa traseira (+ estreita → lanternas nos cantos)
        ]),
        mat: paintMat,
        pos: [0, 0, 0],
        color: "paint",
    });

    // ---- VIDROS: cabine dupla (4 portas) ----
    parts.push({ geo: rakedGlass(1.9, 0.975, -0.92, 1.42, 2.22, 0.32, 0.05), mat: glass, pos: [0, 0, 0] }); // vidro traseiro ~90° (rakeR baixo)

    // ---- ESCURO (clad): grade, para-choques, pods, liner, roll bar, molduras, steps ----
    parts.push({
        geo: mergeGeometries([
            tbox(1.5, 0.66, 0.14, 0, 1.02, 2.28), // GRADE trapezoidal GRANDE (dark)
            tbox(1.98, 0.4, 0.32, 0, 0.52, 2.34), // para-choque dianteiro
            tbox(1.98, 0.44, 0.34, 0, 0.56, -2.91), // para-choque traseiro (atrás da tampa)
            tbox(0.34, 0.34, 0.24, 0.74, 0.54, 2.3), // POD de neblina D (canto inf.)
            tbox(0.34, 0.34, 0.24, -0.74, 0.54, 2.3), // POD de neblina E
            tbox(1.74, 0.1, 1.7, 0, 0.98, -1.9), // piso/liner da caçamba (recuado)
            tbox(0.14, 0.1, 1.9, 1.0, 0.5, -0.25), // estribo (step) D — entre os arcos, sem invadir
            tbox(0.14, 0.1, 1.9, -1.0, 0.5, -0.25), // estribo E
            tbox(0.17, 0.08, 1.86, 0.93, 1.48, -1.9), // rail no topo da mureta D
            tbox(0.17, 0.08, 1.86, -0.93, 1.48, -1.9), // rail no topo da mureta E
            // ROLL BAR / sport bar — hoop VERTICAL junto à cabine + braços diagonais p/ TRÁS.
            // Pés (poste E base da diagonal) APOIAM nos rails PRETOS da caçamba (x≈±0.9, y↓1.42)
            // → tanto o início quanto o fim da diagonal ficam conectados, sem flutuar.
            cyl(0.055, 0.9, 10).translate(0.9, 1.87, -1.2),
            cyl(0.055, 0.9, 10).translate(-0.9, 1.87, -1.2),
            cyl(0.055, 1.86, 10).rotateZ(HALF_PI).translate(0, 2.28, -1.2), // travessa superior
            cyl(0.045, 1.76, 8).rotateZ(HALF_PI).translate(0, 1.86, -1.2), // travessa média
            cyl(0.05, 0.94, 8).rotateX(0.635).translate(0.9, 1.82, -1.48), // braço diagonal D (pé no rail)
            cyl(0.05, 0.94, 8).rotateX(0.635).translate(-0.9, 1.82, -1.48), // braço diagonal E
            // molduras dos faróis — barras HORIZONTAIS alinhadas, flanqueando a grade
            tbox(0.6, 0.24, 0.16, 0.82, 1.14, 2.3), // housing do farol D (escuro)
            tbox(0.6, 0.24, 0.16, -0.82, 1.14, 2.3), // housing do farol E
        ]),
        mat: clad,
        pos: [0, 0, 0],
    });

    // ---- CROMADO: moldura da grade + emblema Toyota + skid ----
    parts.push({
        geo: mergeGeometries([
            tbox(1.6, 0.08, 0.12, 0, 1.37, 2.3), // moldura sup. da grade
            tbox(1.6, 0.08, 0.12, 0, 0.68, 2.3), // moldura inf. da grade
            tbox(0.08, 0.72, 0.12, 0.78, 1.02, 2.3), // moldura lateral D
            tbox(0.08, 0.72, 0.12, -0.78, 1.02, 2.3), // moldura lateral E
            zDisc(0.17, 0.1, 16, 0, 1.06, 2.4), // emblema Toyota (oval)
            tbox(1.05, 0.14, 0.2, 0, 0.4, 2.46), // skid plate
            tbox(0.44, 0.07, 0.12, 0, 1.2, -2.92), // puxador da tampa traseira
        ]),
        mat: chrome,
        pos: [0, 0, 0],
    });

    // ---- FARÓIS SLIM (claros) + DRL ----
    parts.push({
        geo: mergeGeometries([
            tbox(0.46, 0.1, 0.14, 0.82, 1.16, 2.36), // farol D (acento claro, alinhado)
            tbox(0.46, 0.1, 0.14, -0.82, 1.16, 2.36), // farol E
        ]),
        mat: headMat,
        pos: [0, 0, 0],
    });

    // ---- LANTERNAS verticais nos cantos traseiros (proeminentes na face traseira) ----
    parts.push({
        geo: mergeGeometries([tbox(0.16, 0.52, 0.14, -0.92, 1.13, -2.85), tbox(0.16, 0.52, 0.14, 0.92, 1.13, -2.85)]),
        mat: tailMat,
        pos: [0, 0, 0],
    });

    // ---- ARCOS DE RODA pretos (band compartilhado) + wells + MUD FLAPS ----
    const slots = [
        [-0.94, FA],
        [0.94, FA],
        [-0.94, RA],
        [0.94, RA],
    ]; // rodas recuadas (mais pra dentro)
    const front = [[-0.94, FA], [0.94, FA]];
    const rear = [[-0.94, RA], [0.94, RA]];
    {
        const arches = [],
            dark = [];
        for (const [x, z] of slots) {
            // moldura UNIFORME (0.18 em cima e nas laterais): abertura rIn≈pneu, W e yT casam
            arches.push(archBand(x, z, { AY, W: 0.72, yB: 0.34, yT: 1.24, rc: 0.12, rIn: 0.54, depth: 0.48 }));
            dark.push(axleDisc(0.52, 0.14, 18, x * 0.9, AY, z)); // well REDONDO (r=pneu → não passa do chão, sem canto)
        }
        dark.push(tbox(0.05, 0.3, 0.28, 1.0, 0.28, RA - 0.62)); // mud flap tras. D
        dark.push(tbox(0.05, 0.3, 0.28, -1.0, 0.28, RA - 0.62)); // mud flap tras. E
        parts.push({ geo: mergeGeometries(arches), mat: paintMat, pos: [0, 0, 0], color: "paint" }); // arcos na COR DA CARROCERIA
        parts.push({ geo: mergeGeometries(dark), mat: clad, pos: [0, 0, 0] }); // wells + mud flaps escuros
    }

    // ---- RODAS (pickup alta): TRASEIRAS giram no eixo compartilhado; DIANTEIRAS esterçam ----
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => wheelDisc(0.52, 0.42, 18, x, z))), tire, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => alloyAxle(0.35, 0.48, 14, x, AY, z))), rimMat, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => axleDisc(0.1, 0.52, 8, x, AY, z))), clad, AY, RA)); // cubo
    for (const [x, z] of front) parts.push(...steerWheelParts(x, z, AY, [
        { r: 0.52, depth: 0.42, seg: 18, mat: tire },
        { r: 0.35, depth: 0.48, seg: 14, mat: rimMat, alloy: true },
        { r: 0.1, depth: 0.52, seg: 8, mat: clad },
    ]));

    return parts;
}

/* ===================== PORSCHE 911 GT3 RS ===================== */
function buildPorsche({ paintMat, glass, clad, chrome, rimMat, tire, headMat, tailMat }) {
    const parts = [];
    // Porsche 911 (992) GT3 RS — ref docs/cars/porsche. BAIXO e LARGO; para-lamas
    // DIANTEIROS ELEVADOS (humps) com LOUVERS + traseiros LARGOS; fastback; ASA
    // swan-neck GIGANTE (acima do teto); splitter + 3 tomadas; difusor + ESCAPE CENTRAL
    // duplo; saias; RODAS VERMELHAS center-lock + pinças AMARELAS. Eixos: +Z frente.
    const FA = 1.5,
        RA = -1.5;
    // base BRANCA p/ detalhes que acompanham a cor do jogador em tom ESCURO (color:'paintDark')
    // Painted alloys share the restrained paint highlight.
    const pdMat = paintMat.clone();
    const caliper = new THREE.MeshLambertMaterial({ color: "#f4c430" });
    // caixa mais CURVA (só o 911, que é arredondado) — raio maior que o rbox padrão, sem
    // exagerar (o usuário rejeitou arredondamento alto nos outros; aqui é característico).
    const pbox = (w, h, d) => {
        const r = Math.min(0.22, Math.min(w, h, d) * 0.45);
        return noUV(beveledBox(w, h, d, r));
    };
    const tpbox = (w, h, d, x, y, z) => pbox(w, h, d).translate(x, y, z);

    // ---- PINTURA: corpo baixo/CURVO + deck traseiro + teto fastback + para-lamas ----
    parts.push({
        geo: mergeGeometries([
            tpbox(1.86, 0.58, 4.4, 0, 0.66, 0), // corpo baixo e longo (curvo)
            tpbox(1.62, 0.34, 1.7, 0, 1.0, -1.35), // deck traseiro (motor) elevado
            rbox(1.44, 0.1, 0.84).rotateX(-0.1).translate(0, 1.45, 0.04), // teto fastback
            tpbox(0.54, 0.34, 2.2, 0.72, 1.02, 1.0), // para-lama DIANT. D (hump elevado, do cowl ao nariz)
            tpbox(0.54, 0.34, 2.2, -0.72, 1.02, 1.0), // para-lama DIANT. E
            // Keep the hood above the steering envelope and overlap the inner
            // fender shoulders. The old low/narrow edges were carved open.
            pbox(1.16, 0.14, 1.9).rotateX(0.06).translate(0, 1.05, 1.28),
            tpbox(0.52, 0.5, 1.9, 0.82, 0.7, -1.2), // para-lama TRAS. D (recuado ATRÁS do pneu → não atravessa a roda)
            tpbox(0.52, 0.5, 1.9, -0.82, 0.7, -1.2), // para-lama TRAS. E
            tpbox(1.88, 0.24, 0.56, 0, 0.96, -0.3), // PREENCHE o furo da cinta lateral (liga o hump diant. ao deck tras.)
        ]),
        mat: paintMat,
        pos: [0, 0, 0],
        color: "paint",
    });

    // ---- VIDROS: cabine fastback (perfil lateral em 4 pontos, desce longo p/ trás) ----
    {
        const A = [0.95, 1.12],
            B = [0.45, 1.5],
            C = [-0.4, 1.42],
            D = [-1.5, 1.08];
        const s = new THREE.Shape();
        s.moveTo(D[0], D[1]);
        s.lineTo(A[0], A[1]);
        s.lineTo(B[0], B[1]);
        s.lineTo(C[0], C[1]);
        s.closePath();
        const gw = 1.5;
        const g = new THREE.ExtrudeGeometry(s, { depth: gw, bevelEnabled: false });
        g.translate(0, 0, -gw / 2);
        g.rotateY(-HALF_PI);
        parts.push({ geo: noUV(g), mat: glass, pos: [0, 0, 0] });
    }

    // ---- ESCURO (clad): splitter+tomadas, louvers, NACA, difusor, grelha, asa, saias
    parts.push({
        geo: mergeGeometries([
            tbox(2.08, 0.1, 0.52, 0, 0.36, 2.14), // splitter (baixo, largo, avança)
            tbox(0.66, 0.3, 0.16, 0, 0.62, 2.18), // tomada CENTRAL
            tbox(0.34, 0.22, 0.14, -0.66, 0.56, 2.14), // tomada lateral E
            tbox(0.34, 0.22, 0.14, 0.66, 0.56, 2.14), // tomada lateral D
            tbox(0.36, 0.04, 0.6, 0.72, 1.19, 1.2), // LOUVERS do para-lama D (em cima do hump)
            tbox(0.36, 0.04, 0.6, -0.72, 1.19, 1.2), // LOUVERS do para-lama E
            box(0.22, 0.035, 0.72).rotateX(0.06).translate(0.28, 1.132, 1.35), // ducto alinhado ao novo capô D
            box(0.22, 0.035, 0.72).rotateX(0.06).translate(-0.28, 1.132, 1.35), // ducto alinhado ao novo capô E
            zDisc(0.2, 0.1, 16, -0.72, 1.02, 2.12), // aro escuro do farol E (À FRENTE do para-lama → moldura toda visível)
            zDisc(0.2, 0.1, 16, 0.72, 1.02, 2.12), // aro escuro do farol D
            tbox(0.09, 0.28, 0.5, 1.0, 0.94, -0.7), // tomada lateral tras. D (antes da roda)
            tbox(0.09, 0.28, 0.5, -1.0, 0.94, -0.7), // tomada lateral tras. E
            tbox(0.14, 0.16, 2.1, 1.02, 0.42, 0), // saia lateral D
            tbox(0.14, 0.16, 2.1, -1.02, 0.42, 0), // saia lateral E
            tbox(1.02, 0.05, 0.5, 0, 1.13, -1.5), // grelha do motor (deck)
            tbox(1.98, 0.42, 0.46, 0, 0.5, -2.16), // difusor traseiro
            // ASA swan-neck GIGANTE: 2 montantes + plano LARGO alto + 2 endplates
            tbox(0.1, 0.42, 0.14, 0.5, 1.33, -1.62), // montante D (mais curto → asa mais baixa)
            tbox(0.1, 0.42, 0.14, -0.5, 1.33, -1.62), // montante E
            tbox(2.26, 0.08, 0.64, 0, 1.56, -1.76), // plano da asa (~30% mais baixo)
            tbox(0.06, 0.32, 0.64, 1.12, 1.46, -1.76), // endplate D
            tbox(0.06, 0.32, 0.64, -1.12, 1.46, -1.76), // endplate E
        ]),
        mat: clad,
        pos: [0, 0, 0],
    });

    // ---- CROMADO: escape CENTRAL duplo (traseira, baixo/centro) ----
    parts.push({
        geo: mergeGeometries([zDisc(0.1, 0.24, 12, -0.13, 0.36, -2.3), zDisc(0.1, 0.24, 12, 0.13, 0.36, -2.3)]),
        mat: chrome,
        pos: [0, 0, 0],
    });

    // ---- FARÓIS REDONDOS (claros) ----
    parts.push({
        geo: mergeGeometries([zDisc(0.15, 0.12, 16, -0.72, 1.02, 2.16), zDisc(0.15, 0.12, 16, 0.72, 1.02, 2.16)]),
        mat: headMat,
        pos: [0, 0, 0],
    });

    // ---- LANTERNA traseira: MOLDURA escura + barra vermelha RETA na FACE traseira
    // (proeminente z-2.22/-2.26 → à frente do deck do motor, não escondida/torta).
    parts.push({ geo: tbox(1.7, 0.2, 0.06, 0, 0.93, -2.22), mat: clad, pos: [0, 0, 0] }); // moldura/housing
    parts.push({ geo: tbox(1.56, 0.12, 0.06, 0, 0.93, -2.26), mat: tailMat, pos: [0, 0, 0] }); // barra vermelha

    // ---- DETALHES (tom escuro da pintura): pontas do splitter + faixa lateral ----
    parts.push({
        geo: mergeGeometries([
            tbox(0.4, 0.14, 0.12, -0.85, 0.37, 2.42), // ponta do splitter E (PROEMINENTE: à frente/acima do preto, sem z-fighting)
            tbox(0.4, 0.14, 0.12, 0.85, 0.37, 2.42), // ponta do splitter D
            tbox(0.05, 0.14, 1.6, 1.0, 0.66, -0.1), // faixa lateral D
            tbox(0.05, 0.14, 1.6, -1.0, 0.66, -0.1), // faixa lateral E
        ]),
        mat: pdMat,
        pos: [0, 0, 0],
        color: "paintDark",
    });

    // ---- RODAS VERMELHAS (center-lock) + pneu + cubo + pinça AMARELA ----
    const slots = [
        [-0.9, FA],
        [0.9, FA],
        [-0.9, RA],
        [0.9, RA],
    ]; // rodas recuadas (mais pra dentro/flush)
    const front = [[-0.9, FA], [0.9, FA]];
    const rear = [[-0.9, RA], [0.9, RA]];
    const AY = 0.5;
    // CAIXAS DE RODA: FRISO FINO redondo na cor da carroceria (só contorna a roda, sem
    // anel grosso) + well escuro atrás (reentrância) → a roda deixa de "mesclar" no corpo.
    parts.push({
        geo: mergeGeometries(slots.map(([x, z]) => roundArch(x, z, { AY, rIn: 0.56, rOut: 0.63, yB: 0.46, depth: 0.34 }))),
        mat: paintMat,
        pos: [0, 0, 0],
        color: "paint",
    });
    parts.push({ geo: mergeGeometries(slots.map(([x, z]) => axleDisc(0.5, 0.16, 18, x * 0.86, AY, z))), mat: clad, pos: [0, 0, 0] }); // well escuro (reentrância)
    // TRASEIRAS: giro no eixo compartilhado. DIANTEIRAS: pneu + aro (tom escuro) + cubo que ESTERÇAM.
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => wheelDisc(0.5, 0.44, 18, x, z))), tire, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => alloyAxle(0.36, 0.5, 16, x, AY, z))), pdMat, AY, RA, { color: "paintDark" })); // aro no tom escuro da pintura
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => axleDisc(0.09, 0.54, 8, x, AY, z))), clad, AY, RA)); // cubo center-lock
    for (const [x, z] of front) parts.push(...steerWheelParts(x, z, AY, [
        { r: 0.5, depth: 0.44, seg: 18, mat: tire },
        { r: 0.36, depth: 0.5, seg: 16, mat: pdMat, alloy: true, color: "paintDark" },
        { r: 0.09, depth: 0.54, seg: 8, mat: clad },
    ]));
    parts.push({ geo: mergeGeometries(slots.map(([x, z]) => tbox(0.14, 0.3, 0.16, x * 0.84, AY + 0.05, z + 0.3))), mat: caliper, pos: [0, 0, 0], wheelHardware: true }); // pinça AMARELA (frente da roda)

    return parts;
}

/* ===================== TOYOTA RAV4 (crossover SUV) ===================== */
function buildRav4({ paintMat, glass, clad, chrome, rimMat, tire, headMat, tailMat }) {
    const parts = [];
    // Toyota RAV4 Mk6 XA60 2025 — crossover SUV. Marcas: CLADDING PRETO GROSSO/ANGULAR nos
    // arcos + soleira (a cara do RAV4), TETO PRETO bicolor + rails, faróis finos angulares,
    // grade trapezoidal grande + pods de neblina, traseira em rampa c/ lanternas verticais.
    const FA = 1.55,
        RA = -1.55,
        AY = 0.5;
    const aw = [
        [0.94, FA],
        [-0.94, FA],
        [0.94, RA],
        [-0.94, RA],
    ]; // rodas recuadas (mais pra dentro)

    // ---- PINTURA: corpo + capô ----
    parts.push({
        geo: mergeGeometries([
            thbox(2.0, 0.9, 4.6, 0, 0.86, 0), // corpo (y 0.41..1.31), balanços contêm os arcos
            trbox(1.86, 0.14, 1.4, 0, 1.3, 1.4), // capô (z 0.7..2.1)
        ]),
        mat: paintMat,
        pos: [0, 0, 0],
        color: "paint",
    });

    // ---- VIDROS: estufa (para-brisa inclinado + traseiro mais RETO, junto à traseira) ----
    parts.push({ geo: rakedGlass(1.84, 1.05, -2.1, 1.32, 1.9, 0.42, 0.16), mat: glass, pos: [0, 0, 0] });

    // ---- ESCURO (clad): TETO PRETO + cladding (arcos vêm depois) + grade + bumpers +
    //      pods + molduras dos faróis + spoiler + rails + pilares ----
    parts.push({
        geo: mergeGeometries([
            tbox(1.8, 0.16, 2.57, 0, 1.95, -0.655), // TETO PRETO — encaixa do topo do para-brisa (z0.63) ao topo do vidro traseiro (z-1.94)
            tbox(2.06, 0.42, 3.1, 0, 0.48, 0), // cladding inferior (soleira, liga os arcos)
            tbox(1.46, 0.54, 0.16, 0, 0.74, 2.26), // grade trapezoidal GRANDE (baixa)
            tbox(1.98, 0.4, 0.32, 0, 0.42, 2.28), // para-choque dianteiro (cladding baixo)
            tbox(1.98, 0.4, 0.32, 0, 0.42, -2.28), // para-choque traseiro
            tbox(0.28, 0.28, 0.22, 0.8, 0.52, 2.28), // POD de neblina D (angular)
            tbox(0.28, 0.28, 0.22, -0.8, 0.52, 2.28), // POD de neblina E
            tbox(0.66, 0.2, 0.12, 0.66, 1.16, 2.22), // moldura do farol D
            tbox(0.66, 0.2, 0.12, -0.66, 1.16, 2.22), // moldura do farol E
            tbox(1.68, 0.12, 0.26, 0, 1.9, -1.98), // spoiler (topo do vidro traseiro / fim do teto)
            tbox(0.09, 0.09, 2.5, 0.75, 2.05, -0.655), // rail de teto D
            tbox(0.09, 0.09, 2.5, -0.75, 2.05, -0.655), // rail de teto E
            tcbox(0.06, 0.6, 0.12, 0.95, 1.62, 0.1),
            tcbox(0.06, 0.6, 0.12, -0.95, 1.62, 0.1), // pilar B
            tcbox(0.06, 0.56, 0.12, 0.95, 1.6, -1.35),
            tcbox(0.06, 0.56, 0.12, -0.95, 1.6, -1.35), // pilar C
        ]),
        mat: clad,
        pos: [0, 0, 0],
    });

    // ---- CROMADOS: skids + friso da janela ----
    parts.push({
        geo: mergeGeometries([
            tbox(1.12, 0.12, 0.18, 0, 0.36, 2.34), // skid dianteiro
            tbox(1.12, 0.14, 0.18, 0, 0.38, -2.34), // skid traseiro
            tbox(0.05, 0.05, 3.15, 0.93, 1.32, -0.525), // friso da janela D (do para-brisa z1.05 ao vidro traseiro z-2.1)
            tbox(0.05, 0.05, 3.15, -0.93, 1.32, -0.525), // friso da janela E
        ]),
        mat: chrome,
        pos: [0, 0, 0],
    });

    // ---- FARÓIS finos angulares (claros) ----
    parts.push({
        geo: mergeGeometries([tbox(0.6, 0.12, 0.14, 0.66, 1.16, 2.28), tbox(0.6, 0.12, 0.14, -0.66, 1.16, 2.28)]),
        mat: headMat,
        pos: [0, 0, 0],
    });

    // ---- LANTERNAS: verticais nos cantos + faixa que conecta ----
    parts.push({
        geo: mergeGeometries([
            tbox(0.16, 0.44, 0.08, 0.82, 1.08, -2.3),
            tbox(0.16, 0.44, 0.08, -0.82, 1.08, -2.3),
            tbox(1.68, 0.09, 0.06, 0, 1.18, -2.3), // faixa conectando as lanternas
        ]),
        mat: tailMat,
        pos: [0, 0, 0],
    });

    // ---- ARCOS: na COR DA CARROCERIA (archBand) + wells redondos escuros ----
    {
        const arches = [],
            wells = [];
        for (const [x, z] of aw) {
            arches.push(archBand(x, z, { AY, W: 0.68, yB: 0.3, yT: 1.18, rc: 0.08, rIn: 0.54, depth: 0.44 }));
            wells.push(axleDisc(0.5, 0.14, 18, x * 0.9, AY, z)); // well redondo (r=pneu → não passa do chão)
        }
        parts.push({ geo: mergeGeometries(arches), mat: paintMat, pos: [0, 0, 0], color: "paint" }); // arcos na COR DA CARROCERIA
        parts.push({ geo: mergeGeometries(wells), mat: clad, pos: [0, 0, 0] });
    }

    // ---- RODAS: TRASEIRAS giram no eixo compartilhado; DIANTEIRAS (pneu+aro+cubo) esterçam ----
    const rrear = [[0.94, RA], [-0.94, RA]];
    const rfront = [[0.94, FA], [-0.94, FA]];
    parts.push(rearAxlePart(mergeGeometries(rrear.map(([x, z]) => wheelDisc(0.5, 0.44, 18, x, z))), tire, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(rrear.map(([x, z]) => alloyAxle(0.34, 0.5, 16, x, AY, z))), rimMat, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(rrear.map(([x, z]) => axleDisc(0.1, 0.54, 8, x, AY, z))), clad, AY, RA));
    for (const [x, z] of rfront) parts.push(...steerWheelParts(x, z, AY, [
        { r: 0.5, depth: 0.44, seg: 18, mat: tire },
        { r: 0.34, depth: 0.5, seg: 16, mat: rimMat, alloy: true },
        { r: 0.1, depth: 0.54, seg: 8, mat: clad },
    ]));

    return parts;
}

/* ===================== POLESTAR 4 (fastback EV) ===================== */
function buildPolestar({ paintMat, glass, clad, chrome, rimMat, tire, headMat, tailMat }) {
    const parts = [];
    // Polestar 4 2023 — SUV-coupé fastback EV. Marcas: silhueta fastback LONGA e fluida,
    // faróis DIVIDIDOS (blade DRL + luz menor), SEM grade (frente lisa + tomada baixa),
    // FAIXA DE LUZ traseira full-width, cladding preto SUTIL, rodas aero grandes, ducktail.
    const FA = 1.55,
        RA = -1.55,
        AY = 0.52;
    const aw = [
        [0.84, FA],
        [-0.84, FA],
        [0.84, RA],
        [-0.84, RA],
    ]; // rodas RECUADAS (tuck-in: pneu ~flush)

    // ---- PINTURA: corpo inferior + ombro/capô/deck (stance de crossover-coupé) ----
    parts.push({
        geo: mergeGeometries([
            thbox(2.0, 0.74, 4.7, 0, 0.73, 0), // corpo inferior (y 0.36..1.10)
            thbox(1.9, 0.44, 4.7, 0, 1.14, 0), // ombro/capô/deck (frente z2.35 + traseira z-2.35: faróis e lanterna colam)
            trbox(1.76, 0.16, 1.3, 0, 1.95, -0.22), // TETO na cor do carro (define o design; sobre a seção de teto da estufa)
        ]),
        mat: paintMat,
        pos: [0, 0, 0],
        color: "paint",
    });

    // ---- VIDROS: cabine fastback LONGA (perfil 4 pontos: para-brisa → pico → fastback) ----
    {
        const A = [1.05, 1.36],
            B = [0.35, 1.96],
            C = [-0.8, 1.9],
            D = [-2.25, 1.36]; // base RETA (cintura y1.36)
        const s = new THREE.Shape();
        s.moveTo(D[0], D[1]);
        s.lineTo(A[0], A[1]); // base (cintura)
        s.lineTo(B[0], B[1]); // para-brisa sobe até o pico
        s.lineTo(C[0], C[1]); // teto quase reto
        s.closePath(); // C→D = fastback LONGO até quase a traseira
        const gw = 1.72;
        const g = new THREE.ExtrudeGeometry(s, { depth: gw, bevelEnabled: false });
        g.translate(0, 0, -gw / 2);
        g.rotateY(-HALF_PI);
        parts.push({ geo: noUV(g), mat: glass, pos: [0, 0, 0] });
    }

    // ---- ESCURO (clad): cladding SUTIL + fascia/tomada + difusor + ducktail + molduras +
    //      pilares ----
    parts.push({
        geo: mergeGeometries([
            tbox(2.02, 0.26, 3.3, 0, 0.4, 0), // cladding inferior SUTIL (soleira)
            tbox(1.92, 0.32, 0.3, 0, 0.4, 2.3), // fascia dianteira baixa
            tbox(1.3, 0.2, 0.12, 0, 0.62, 2.34), // tomada de ar baixa (EV, sem grade)
            tbox(0.14, 0.34, 0.18, 0.8, 0.54, 2.32), // air curtain (canto D)
            tbox(0.14, 0.34, 0.18, -0.8, 0.54, 2.32), // air curtain (canto E)
            tbox(1.74, 0.3, 0.34, 0, 0.42, -2.3), // difusor traseiro
            tbox(1.72, 0.1, 0.2, 0, 1.42, -2.28), // spoiler ducktail (colado na traseira reta)
            tbox(0.64, 0.14, 0.1, 0.68, 1.16, 2.3), // moldura do farol D
            tbox(0.64, 0.14, 0.1, -0.68, 1.16, 2.3), // moldura do farol E
            // (sem pilares: estufa frameless/vidro escuro contínuo — visual sleek do Polestar)
        ]),
        mat: clad,
        pos: [0, 0, 0],
    });

    // ---- CROMADOS: friso da janela + skids ----
    parts.push({
        geo: mergeGeometries([
            tbox(0.05, 0.05, 3.35, 0.9, 1.36, -0.625), // friso da janela D (do para-brisa z1.05 até a traseira z-2.3)
            tbox(0.05, 0.05, 3.35, -0.9, 1.36, -0.625), // friso da janela E
            tbox(1.0, 0.08, 0.14, 0, 0.3, 2.35), // skid dianteiro
            tbox(1.0, 0.08, 0.14, 0, 0.32, -2.35), // skid traseiro
            tbox(1.42, 0.05, 0.1, 0, 0.5, 2.38), // blade horizontal (splitter) da frente
            tbox(0.05, 0.14, 0.08, 0.42, 0.63, 2.39),
            tbox(0.05, 0.14, 0.08, 0.14, 0.63, 2.39), // aletas verticais da grade (detalhe)
            tbox(0.05, 0.14, 0.08, -0.14, 0.63, 2.39),
            tbox(0.05, 0.14, 0.08, -0.42, 0.63, 2.39),
        ]),
        mat: chrome,
        pos: [0, 0, 0],
    });

    // ---- FARÓIS DIVIDIDOS: blade DRL fina em cima + luz menor embaixo (claros) ----
    parts.push({
        geo: mergeGeometries([
            tbox(0.56, 0.06, 0.12, 0.68, 1.2, 2.34), // blade DRL D
            tbox(0.56, 0.06, 0.12, -0.68, 1.2, 2.34), // blade DRL E
            tbox(0.2, 0.12, 0.12, 0.82, 1.04, 2.34), // luz menor D (split)
            tbox(0.2, 0.12, 0.12, -0.82, 1.04, 2.34), // luz menor E
        ]),
        mat: headMat,
        pos: [0, 0, 0],
    });

    // ---- FAIXA DE LUZ traseira full-width (assinatura Polestar) + asas nos cantos ----
    parts.push({
        geo: mergeGeometries([
            tbox(1.94, 0.1, 0.1, 0, 1.28, -2.33), // faixa contínua (colada na traseira reta)
            tbox(0.16, 0.26, 0.1, 0.86, 1.14, -2.33), // asa (wing) D
            tbox(0.16, 0.26, 0.1, -0.86, 1.14, -2.33), // asa (wing) E
        ]),
        mat: tailMat,
        pos: [0, 0, 0],
    });

    // ---- ARCOS: QUADRADOS com border-radius, na COR DA CARROCERIA + wells escuros ----
    {
        const arches = [],
            wells = [];
        for (const [x, z] of aw) {
            arches.push(archBand(x, z, { AY, W: 0.64, yB: 0.34, yT: 1.16, rc: 0.2, rIn: 0.56, depth: 0.44 }));
            wells.push(axleDisc(0.52, 0.14, 18, x * 0.86, AY, z)); // well redondo (r=pneu → não passa do chão)
        }
        parts.push({ geo: mergeGeometries(arches), mat: paintMat, pos: [0, 0, 0], color: "paint" }); // arcos na COR DA CARROCERIA
        parts.push({ geo: mergeGeometries(wells), mat: clad, pos: [0, 0, 0] });
    }

    // ---- RODAS aero (recuadas, ~flush): TRASEIRAS giram no eixo compartilhado; DIANTEIRAS esterçam ----
    const prear = [[0.84, RA], [-0.84, RA]];
    const pfront = [[0.84, FA], [-0.84, FA]];
    parts.push(rearAxlePart(mergeGeometries(prear.map(([x, z]) => wheelDisc(0.52, 0.42, 18, x, z))), tire, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(prear.map(([x, z]) => alloyAxle(0.36, 0.44, 16, x, AY, z))), rimMat, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(prear.map(([x, z]) => axleDisc(0.1, 0.48, 8, x, AY, z))), clad, AY, RA));
    for (const [x, z] of pfront) parts.push(...steerWheelParts(x, z, AY, [
        { r: 0.52, depth: 0.42, seg: 18, mat: tire },
        { r: 0.36, depth: 0.44, seg: 16, mat: rimMat, alloy: true },
        { r: 0.1, depth: 0.48, seg: 8, mat: clad },
    ]));

    return parts;
}

/* ===================== VW FUSCA (Beetle clássico) ===================== */
function buildFusca({ paintMat, glass, clad, chrome, rimMat, tire, headMat, tailMat }) {
    const parts = [];
    // Volkswagen Fusca clássico — ref docs/cars/fusca. Silhueta em CÚPULA contínua
    // (capô arqueado caindo p/ o nariz + teto em arco descendo até a tampa do motor),
    // PARA-LAMAS BULBOSOS separados do corpo com FARÓIS REDONDOS em pé, piscas âmbar
    // no topo do para-lama, ESTRIBOS (running boards), para-choques CROMADOS de
    // lâmina, janela traseira PEQUENA deitada na cúpula + grelhas do motor, rodas de
    // aço com CALOTA cromada. Carro CURTO (entre-eixos 2.64) e estreito. +Z = frente.
    const FA = 1.32,
        RA = -1.32,
        AY = 0.365; // Ø do pneu = 0.42 da altura do carro (medido no desenho: 0.425;
    // a 0.42 de raio dava 0.49 = pneu 15% grande, carro "empoleirado"). Com a soleira
    // da carroceria em 0.36, o flanco encosta na altura do centro de roda, como no real.
    const WX = 0.6; // bitola: para-lama GORDO (0.54) fundido num corpo LARGO (1.44 —
    // visto de cima o Fusca é "gordinho", corpo fino lia como carro estreito);
    // largura total ~1.75, L/A ~1.06 (real 1.03)
    const slots = [
        [-WX, FA],
        [WX, FA],
        [-WX, RA],
        [WX, RA],
    ];
    const front = [[-WX, FA], [WX, FA]];
    const rear = [[-WX, RA], [WX, RA]];
    // pisca âmbar sobre o para-lama (marca do Fusca BR); Basic como farol/lanterna
    const amber = new THREE.MeshBasicMaterial({ color: "#ffb03a" });
    // caixa BEM arredondada (só o Fusca: nele tudo é bojudo de fábrica; os demais
    // modelos seguem crisp)
    const vbox = (w, h, d) => {
        const r = Math.min(0.2, Math.min(w, h, d) * 0.45);
        return noUV(beveledBox(w, h, d, r));
    };
    const tvbox = (w, h, d, x, y, z) => vbox(w, h, d).translate(x, y, z);
    // casca de PERFIL CURVO: arco quadrático (3 primeiros pontos) + retas de fecho,
    // extrudada em X — capô, cúpula traseira e teto saem de arcos DE VERDADE, não
    // de caixas inclinadas. Pontos em (z,y), como rakedGlass.
    const shell = (w, pts) => {
        const s = new THREE.Shape();
        s.moveTo(pts[0][0], pts[0][1]);
        s.quadraticCurveTo(pts[1][0], pts[1][1], pts[2][0], pts[2][1]);
        for (let i = 3; i < pts.length; i++) s.lineTo(pts[i][0], pts[i][1]);
        s.closePath();
        const g = new THREE.ExtrudeGeometry(s, { depth: w, bevelEnabled: false, curveSegments: curveSeg(12) });
        g.translate(0, 0, -w / 2);
        g.rotateY(-HALF_PI); // perfil → mundo (shapeX→Z, shapeY→Y, extrude→X)
        return noUV(g);
    };

    // TETO+CÚPULA numa casca SÓ (perfil contínuo — 2 quadráticas com a MESMA tangente
    // na emenda z-0.85; separado em 2 shells dava "corcova dupla" com degrau): arco
    // RASO do teto (topo do para-brisa → sobre o vidro traseiro) e queda da cúpula até
    // a tampa do motor. Borda inferior ALTA (1.40-1.46) na região dos vidros p/ as
    // janelas laterais terem a MESMA altura do para-brisa.
    //
    // ⚠️ A casca é um LOFT, não uma extrusão. A ExtrudeGeometry só tem 2 anéis em X
    // (os dois caps): o topo fica CHAPADO na largura, a transição teto→flanco é uma
    // quina de 90° e — pior — os caps são triangulados por EARCUT em triângulos
    // longos; ao deformar o X vértice a vértice p/ afinar a cúpula esses triângulos
    // deixam de ser planos e DOBRAM, produzindo as cunhas escuras de aresta reta
    // ("teto quebrado" sob o vidro traseiro, 4 relatos do usuário). Aqui a malha é
    // uma GRADE regular estação×seção: seção SUPERELÍPTICA (topo abaulado + quina
    // arredondada de verdade) e largura variando suave ao longo de z.
    // (o expoente da superelipse varia por espessura de seção — ver shellSec)
    // perfis do teto (topo) e da face inferior (beiral → coluna C → barriga)
    const qsample = (p0, p1, p2, n) => {
        const out = [];
        for (let i = 0; i <= n; i++) {
            const t = i / n,
                u = 1 - t;
            out.push([u * u * p0[0] + 2 * u * t * p1[0] + t * t * p2[0], u * u * p0[1] + 2 * u * t * p1[1] + t * t * p2[1]]);
        }
        return out;
    };
    const ROOF_TOP = [
        // ⚠️ FACE FRONTAL INCLINADA (z0.46,y1.55 → z0.43,y1.63): é o que fecha o teto
        // sobre o topo do para-brisa. Sem ela o teto começa reto em z0.43 e o vidro e
        // a coluna A ficam À FRENTE dele → fresta vazada acima do para-brisa.
        [0.35, 1.55],
        // teto: crista RECUADA (ctrl −0.55) e cabine +0.12 mais longa — o desenho
        // técnico mostra o teto ainda alto sobre o eixo traseiro; antes ele desabava
        // 265 mm cedo demais. O para-brisa também reclinou (37°), então a borda
        // dianteira do teto veio de z0.43 para 0.32.
        ...qsample([0.32, 1.63], [-0.55, 1.88], [-1.25, 1.47], 20),
        ...qsample([-1.25, 1.47], [-1.62, 1.21], [-1.88, 0.56], 20).slice(1), // cauda até a altura do para-choque
    ];
    const ROOF_BOT = [
        // ⚠️ o beiral tem que ficar ABAIXO do topo do vidro em TODO o vão (o vidro
        // entra ~0.035 na chapa). Beiral acima do vidro = FRESTA vazada entre teto e
        // janela — foi o que aconteceu ao mexer na cabine.
        ...qsample([0.35, 1.55], [-0.45, 1.7], [-1.08, 1.36], 16), // beiral sobre as janelas
        [-1.26, 0.95], // coluna C em rampa (recuada junto com a cabine)
        [-1.88, 0.48], // barriga (oculta)
    ];
    const polyAt = (poly, z) => {
        if (z >= poly[0][0]) return poly[0][1];
        for (let i = 1; i < poly.length; i++) {
            if (z >= poly[i][0]) {
                const [z0, y0] = poly[i - 1],
                    [z1, y1] = poly[i];
                return y0 + (y1 - y0) * ((z - z0) / (z1 - z0));
            }
        }
        return poly[poly.length - 1][1];
    };
    const shellA = (z) => {
        // largura em PLANTA: a cauda afina p/ o ovo (ref VW_Fusca_ref_traseira_oval)
        const t = Math.min(1, Math.max(0, (-0.9 - z) / 0.98));
        return 0.65 * (1 - 0.14 * t * t * (3 - 2 * t));
    };
    const shellSec = (z) => {
        const yt = polyAt(ROOF_TOP, z);
        const b = Math.max(yt - polyAt(ROOF_BOT, z), 0.05); // espessura mínima na ponta
        // expoente por ESPESSURA: seção fina (painel do teto) fica CHATA no topo com
        // quina rolada (n=5) — com n=3 ela afinava cedo e abria entalhe escuro no
        // canto do para-brisa; seção gorda (cauda) segue em ovo (n=3).
        const n = 3 + 2 * Math.min(1, Math.max(0, (0.25 - b) / 0.2));
        return { yt, yb: yt - b, b, a: shellA(z), n };
    };
    /** Meia-largura da casca em (y,z) — FONTE ÚNICA: vidros e colunas seguem daqui. */
    const shellHalf = (y, z) => {
        if (z > 0.35) return Infinity; // à frente da casca: sem restrição
        const { yb, b, a, n } = shellSec(z);
        const t = Math.min(1, Math.max(0, (y - yb) / b));
        return a * Math.pow(Math.max(0, 1 - Math.pow(t, n)), 1 / n);
    };
    /**
     * Apara uma peça pelo VOLUME da casca: corta o topo na face INFERIOR do teto e
     * a largura pelo perfil. Sem o corte em Y a peça entra no sólido do teto e a
     * face inferior dele (um plano que cruza toda a largura) atravessa a peça,
     * aparecendo como cunha vermelha em V — "o teto cortando a coluna".
     */
    const clampToShell = (g, margin = 0.005) => {
        const p = g.attributes.position;
        for (let i = 0; i < p.count; i++) {
            const z = p.getZ(i);
            if (z > 0.35) continue; // à frente da casca: livre
            const { yb } = shellSec(z);
            const y = p.getY(i);
            if (y > yb - margin) p.setY(i, yb - margin);
            const lim = shellHalf(Math.min(y, yb - margin), z) - margin;
            const x = p.getX(i);
            if (Number.isFinite(lim) && Math.abs(x) > lim) p.setX(i, Math.sign(x) * lim);
        }
        g.computeVertexNormals();
        return g;
    };
    /** Altura da SUPERFÍCIE da casca em (x,z) — p/ assentar peças na cauda curva. */
    const shellY = (x, z) => {
        const { yb, b, a, n } = shellSec(z);
        const t = Math.min(1, Math.abs(x) / a);
        return yb + b * Math.pow(Math.max(0, 1 - Math.pow(t, n)), 1 / n);
    };
    /**
     * Painel CURVO colado na casca (vigia, tampa): grade regular que segue a
     * superfície, deslocada `lift` p/ fora. Peça PLANA numa casca curva tem as
     * pontas furando a lataria (a vigia retangular aparecia rasgando a cauda).
     * `xh(u)` = meia-largura em cada estação (u=0 na frente do painel), `xc` = centro
     * em X. `lift` PEQUENO (~0.004): a superfície da cauda é a própria silhueta na
     * vista lateral, então todo painel proud rompe o contorno pelo valor do lift.
     */
    const shellPatch = (zA, zB, xh, lift, nz, nx, xc = 0) => {
        const pos = [],
            idx = [];
        for (let i = 0; i <= nz; i++) {
            const u = i / nz,
                z = zA + (zB - zA) * u,
                w = xh(u);
            for (let j = 0; j <= nx; j++) {
                const x = xc - w + (2 * w * j) / nx;
                pos.push(x, shellY(x, z) + lift, z);
            }
        }
        const v = (i, j) => i * (nx + 1) + j;
        for (let i = 0; i < nz; i++)
            for (let j = 0; j < nx; j++) idx.push(v(i, j), v(i, j + 1), v(i + 1, j), v(i, j + 1), v(i + 1, j + 1), v(i + 1, j));
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
        g.setIndex(idx);
        g.computeVertexNormals();
        return noUV(g);
    };
    const roofShell = (() => {
        const M = cylSeg(12); // pontos do arco (flanco→topo→flanco)
        // ⚠️ estações ADENSADAS na TESTEIRA. A seção sai de uma lâmina de 0.05 em
        // z0.35 e engrossa para 0.086 em 0.09 de z; com passo uniforme (0.13 no jogo)
        // a 1ª estação real cai em z0.22 e a malha corta RETO de uma ponta à outra,
        // afundando ~4 cm ABAIXO da superfície analítica bem sobre o para-brisa. O
        // vidro (e a coluna A) atravessavam esse rebaixo e apareciam por cima do teto
        // como um entalhe escuro no canto — não era buraco, era o teto afundado.
        const S = cylSeg(28);
        const zs = [0.35, 0.338, 0.326, 0.314, 0.3, 0.28];
        for (let i = 0; i <= S; i++) zs.push(0.26 + (-1.88 - 0.26) * (i / S));
        const N = zs.length - 1;
        const pos = [],
            idx = [];
        for (let i = 0; i <= N; i++) {
            const z = zs[i];
            const { yb, b, a, n } = shellSec(z);
            for (let j = 0; j <= M; j++) {
                const th = Math.PI * (j / M),
                    c = Math.cos(th);
                pos.push(a * Math.sign(c) * Math.pow(Math.abs(c), 2 / n), yb + b * Math.pow(Math.sin(th), 2 / n), z);
            }
        }
        const v = (i, j) => i * (M + 1) + j;
        for (let i = 0; i < N; i++)
            for (let j = 0; j < M; j++) idx.push(v(i, j), v(i + 1, j), v(i, j + 1), v(i + 1, j), v(i + 1, j + 1), v(i, j + 1));
        // faces de fechamento com vértices PRÓPRIOS (compartilhá-los faria a média de
        // normais amaciar a aresta do beiral e escurecer o flanco)
        const dup = (i, j) => {
            const k = pos.length / 3;
            pos.push(pos[v(i, j) * 3], pos[v(i, j) * 3 + 1], pos[v(i, j) * 3 + 2]);
            return k;
        };
        for (let i = 0; i < N; i++) {
            const a0 = dup(i, 0), a1 = dup(i, M), b0 = dup(i + 1, 0), b1 = dup(i + 1, M);
            idx.push(a0, a1, b1, a0, b1, b0); // fundo plano (oculto sobre as janelas)
        }
        for (const i of [0, N]) {
            const ring = [];
            for (let j = 0; j <= M; j++) ring.push(dup(i, j));
            for (let j = 1; j < M; j++) idx.push(...(i === 0 ? [ring[0], ring[j], ring[j + 1]] : [ring[0], ring[j + 1], ring[j]]));
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
        g.setIndex(idx);
        g.computeVertexNormals();
        return noUV(g);
    })();

    // PARA-LAMA de meio-TORO: anel vertical (plano ZY) em volta da roda, aberto
    // embaixo — crista REDONDA caindo p/ os DOIS lados (o bojo real do Fusca).
    // A banda extrudada (roundArch) dos outros carros tem topo chato de caixa.
    /**
     * Loft genérico ao longo de Z (mesma malha regular do teto): em cada estação a
     * seção é superelíptica no topo e plana embaixo, definida por f(z) →
     * {yt, yb, a, xc}. Usado no PARA-LAMA DIANTEIRO e no CAPÔ, que precisam de
     * largura e altura variáveis — coisa que `shell` (extrusão de largura fixa) e
     * o toro (arco simétrico) não fazem.
     */
    const zLoft = (z0, z1, nz, nx, f, n = 3) => {
        if (z1 > z0) throw new Error("zLoft: z0 deve ser MAIOR que z1 (estações de frente p/ trás) — invertido, a casca renderiza pelo avesso");
        const pos = [],
            idx = [];
        for (let i = 0; i <= nz; i++) {
            const z = z0 + (z1 - z0) * (i / nz);
            const { yt, yb, a, xc } = f(z);
            const b = Math.max(yt - yb, 0.02);
            for (let j = 0; j <= nx; j++) {
                const th = Math.PI * (j / nx),
                    c = Math.cos(th);
                pos.push(xc + a * Math.sign(c) * Math.pow(Math.abs(c), 2 / n), yb + b * Math.pow(Math.sin(th), 2 / n), z);
            }
        }
        const v = (i, j) => i * (nx + 1) + j;
        for (let i = 0; i < nz; i++) {
            for (let j = 0; j < nx; j++) idx.push(v(i, j), v(i + 1, j), v(i, j + 1), v(i + 1, j), v(i + 1, j + 1), v(i, j + 1));
            idx.push(v(i, 0), v(i, nx), v(i + 1, nx), v(i, 0), v(i + 1, nx), v(i + 1, 0)); // fundo (oculto)
        }
        for (let j = 1; j < nx; j++) idx.push(v(0, 0), v(0, j), v(0, j + 1), v(nz, 0), v(nz, j + 1), v(nz, j)); // tampas
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
        g.setIndex(idx);
        g.computeVertexNormals();
        return noUV(g);
    };
    /** interpola numa tabela [[z,y],…] com z DECRESCENTE */
    const tblAt = (arr, z) => {
        if (z >= arr[0][0]) return arr[0][1];
        for (let i = 1; i < arr.length; i++)
            if (z >= arr[i][0]) {
                const [z0, y0] = arr[i - 1],
                    [z1, y1] = arr[i];
                return y0 + (y1 - y0) * ((z - z0) / (z1 - z0));
            }
        return arr[arr.length - 1][1];
    };
    const fenderTorus = (x, z, R = 0.5) => {
        // toro ACHATADO: tubo circular 0.13 ESCALADO 2.1× ao longo do eixo da roda →
        // seção elíptica 0.26 radial × ~0.55 axial = para-lama GORDO (1/3 da largura
        // do carro) abraçando o pneu quase sem folga. O tubo fino (0.11) lia como
        // "aro de Ford T" descolado — era o maior traço de calhambeque.
        // R menor nos TRASEIROS (0.45): a tampa do motor aparece ACIMA deles no perfil.
        const tubeR = 0.13,
            W = 2.1,
            lift = -0.12; // arco < 180°: pontas ACIMA do eixo (descendo demais virava "saia" escondendo a roda)
        const g = new THREE.TorusGeometry(R, tubeR, cylSeg(10), curveSeg(26), Math.PI + 2 * lift);
        g.rotateZ(-lift); // centraliza a abertura p/ baixo
        g.scale(1, 1, W); // engorda AO LONGO do eixo da roda (Z do anel, antes do rotateY)
        g.rotateY(HALF_PI); // anel XY → ZY (em volta do eixo da roda)
        return noUV(g.translate(x, AY, z));
    };

    // ---- PINTURA: tub central + aventais + capô + teto/cúpula + colunas + para-lamas ----
    parts.push({
        geo: mergeGeometries([
            // TUB com TUMBLEHOME: o flanco recolhe ~6% no terço alto (a caixa reta com
            // fillet lia como "cano" no ombro; o Fusca fecha para o teto)
            (() => {
                const g = tvbox(1.44, 0.8, 2.45, 0, 0.76, -0.275);
                const p = g.attributes.position;
                for (let i = 0; i < p.count; i++) {
                    const t = Math.min(1, Math.max(0, (p.getY(i) - 0.9) / 0.26));
                    if (t > 0) p.setX(i, p.getX(i) * (1 - 0.06 * t * t));
                }
                g.computeVertexNormals();
                return g;
            })(),
            // avental dianteiro RECUADO e baixo (projetado à frente sob o capô lia
            // como "focinho de 2CV"; o capô é o volume mais avançado, o para-choque
            // flutua à frente em suportes)
            tvbox(1.05, 0.36, 0.95, 0, 0.47, 1.42), // avental ESTREITO e baixo (y 0.29..0.65): largo demais virava um escudo chapado entre os faróis
            tbox(1.2, 0.36, 0.7, 0, 0.42, -1.6), // rabeta CRISP escondida sob a cauda
            // CAPÔ em LOFT: afunila em Z (0.82 de largura no cowl → bico de 0.18) e a
            // crista cai em curva CONTÍNUA até y0.52 no bico. A extrusão de largura
            // FIXA (shell) terminava num degrau horizontal e, larga demais, matava o
            // VALE entre capô e crista do para-lama — a frente lia como parede chata.
            // crista do capô SEMPRE ~0.02 abaixo da crista do para-lama (no Fusca o
            // capô MERGULHA entre os para-lamas; acima deles vira quilha de barco)
            // ⚠️ z DECRESCENTE (1.88 → 0.58): o zLoft orienta os triângulos assumindo
            // essa ordem. Chamado ao contrário, as normais apontam para DENTRO, as
            // faces externas são descartadas e o capô fica "transparente" (vê-se o
            // interior). As tabelas são função de z, então servem nas duas ordens.
            zLoft(1.92, 0.58, cylSeg(18), cylSeg(10), (z) => ({
                // CAPÔ PROEMINENTE: na foto de referência a crista do capô é o ponto
                // MAIS ALTO da frente — ela sobe ACIMA da linha dos para-lamas no meio
                // do painel (5 a 7 cm), e o "vale" capô↔para-lama é justamente essa
                // diferença. Antes ele apenas encostava na crista do pontão e a frente
                // lia como um plano só. Extremos PRESOS: o bico (0.67, sobre o
                // para-choque) e a emenda do cowl (1.21, nivelada com a soleira 1.20 —
                // subir aí engoliria a base do para-brisa); todo o ganho vai para a
                // FLECHA do arco. Ficar acima do pontão só é seguro porque o capô é
                // LARGO (a≈0.67): estreito, viraria quilha de barco.
                yt: tblAt([[1.92, 0.67], [1.8, 0.92], [1.6, 1.08], [1.32, 1.15], [1.05, 1.2], [0.86, 1.22], [0.58, 1.21]], z),
                // saias descem no MEIO (fecham a trincheira com o para-lama), mas o
                // BICO é raso (yb 0.50 vs yt 0.62): seção alta + estreita na ponta
                // fazia o capô virar uma QUILHA/lâmina entre os pontões
                yb: tblAt([[1.92, 0.5], [1.6, 0.44], [1.0, 0.48], [0.58, 0.58]], z),
                // largura: o capô ENCOSTA na aba interna dos para-lamas (0.45) — assim
                // o "vale" é a emenda das duas superfícies curvas, não um cânion com
                // parede vertical à mostra
                // largura: SOBREPÕE a aba interna do pontão (0.6−0.22=0.38) — o capô
                // tem que ocupar o miolo do carro; parando na aba ele ficava fino
                // espremido entre os para-lamas gordos
                // planta: LARGA quase até o fim e arredondando só no bico (0.28 = ~55 cm
                // reais, medido na foto). Afunilar até 0.14 dava a planta em cunha.
                a: tblAt([[1.92, 0.36], [1.8, 0.46], [1.5, 0.59], [1.1, 0.67], [0.58, 0.69]], z),
                xc: 0,
            }), 4.3), // expoente: planalto largo (o que se lê como capô) MAS com queda
            // real para os flancos — n=5 era quase uma mesa (3,6% de flecha a 70% da
            // meia-largura), n=4.3 dá ~5,5% = +50% de coroa transversal. Abaixo de ~3
            // a elipse despenca do centro e o capô volta a parecer fino.
            // (SEM coroa separada: dois lofts sobrepostos, ambos de topo achatado,
            // TERRACEAVAM o capô — degraus, depressões e paredes à mostra. O capô
            // agora é UM domo só, com seção arredondada, e o vinco central vem da
            // própria curvatura.)
            // (sem painel da tampa do motor: mesma cor da casca, só existia pelo degrau
            // do lift — que rompia a silhueta; a cauda em loft já lê como tampa)
            roofShell,
            // colunas A (rake ~19°; face externa 0.015 ALÉM do vidro — coplanar serrilhava)
            // PARA-BRISA = RASGO NA LATARIA, não moldura aplicada. As peças abaixo são
            // CHAPA: montantes GROSSOS (0.16 em Z) que também formam o POSTE DE CANTO
            // (cobrem a borda dianteira do vidro lateral) e entram no teto em cima e na
            // cintura embaixo; travessa que penetra o teto. A face frontal delas é
            // COPLANAR com a face do teto e do cowl — quem recua é o VIDRO (0.025).
            // Barras finas proud do vidro liam como quadro de jipe/santantônio.
            // Sem travessa: com o vidro recuado, a BORDA DO TETO já é o cabeçote (uma
            // barra ali só voltava a parecer quadro aparafusado). Fillet pequeno (0.02)
            // p/ o poste ter aresta de chapa, não cara de tubo. O topo é APARADO pelo
            // perfil da casca (shellHalf) → o poste morre DENTRO do teto em vez de
            // espetar pelos flancos onde a cúpula estreita.
            // x0.5975 ± 0.0475 → face externa em 0.645, encostando no flanco da casca
            // (a=0.65). Em 0.635 sobrava uma fresta de 1,5 cm entre o poste e a aba do
            // teto e via-se o ESCURO do interior pelo canto do para-brisa.
            ...[0.5975, -0.5975].map((sx) => {
                // ⚠️ POLÍGONO no plano ZY — NÃO caixa girada + aparo. O clampToShell
                // empurra o vértice para BAIXO mantendo o z; numa caixa INCLINADA isso
                // DECEPA a cunha dianteira do topo, e sobravam ~10 cm (em z) de teto
                // sem coluna embaixo, entre o topo do poste (z0.24) e a TESTEIRA do
                // teto (z0.35): pela frente o teto lia como capota solta pousada sobre
                // o carro, com um vão escuro no canto do para-brisa.
                // Aqui o contorno já nasce com o topo dentro do teto e nada é aparado.
                // ⚠️ a face externa tem que ser PARALELA ao plano do vidro (mesmo rake
                // 0.6905), não uma reta pé→topo. Antes o topo era ancorado em z0.348
                // (sob a testeira) e o pé no rake: as duas faces CRUZAVAM em y1.40 e da
                // metade do para-brisa para cima o VIDRO passava à frente da coluna —
                // aparecia um degrau preto para fora de cada lado e a coluna parecia
                // acabar no meio do para-brisa (2 estruturas conflitando).
                const zf = (y) => 0.615 - 0.6905 * (y - 1.16); // 0.015 SEMPRE à frente do vidro
                // ⚠️ e SEM clampToShell aqui: os caps da extrusão são triangulados por
                // EARCUT (triângulos longos), e mexer no y dos vértices depois DOBRA
                // esses triângulos — a face externa da coluna ganhava vinco/entalhe.
                const s = new THREE.Shape();
                s.moveTo(zf(1.02), 1.02); // pé dianteiro, enterrado no tub/cowl
                // topo: começa onde a face frontal ENCONTRA a face inferior do teto
                // (z0.376 — à frente da testeira o shellSec devolve a seção mínima, então
                // o encontro é em y1.506, no mesmo nível do beiral: a coluna emenda na
                // aba do teto em vez de espetar por cima dela).
                for (let i = 0; i <= 7; i++) {
                    const z = 0.376 + (0.14 - 0.376) * (i / 7);
                    // ⚠️ o topo mira a FACE INFERIOR da casca (shellSec.yb — um plano
                    // horizontal em toda a largura) e entra 6 mm nela. Não serve mirar
                    // em ROOF_BOT (na testeira o yb desce 0.05 abaixo dela, por
                    // espessura mínima) nem em shellY: shellY é a curva ANALÍTICA e a
                    // malha do teto é um polígono de poucos segmentos, cuja CORDA
                    // afunda ~3 cm perto do flanco — o poste ficava sob a curva e ainda
                    // assim ESPETAVA pela corda, virando uma aba solta no canto.
                    s.lineTo(z, shellSec(z).yb + 0.006);
                }
                s.lineTo(zf(1.02) - 0.188, 1.02); // pé traseiro (espessura ~0.155 perpendicular)
                s.closePath();
                const gp = new THREE.ExtrudeGeometry(s, { depth: 0.095, bevelEnabled: false });
                gp.translate(0, 0, -0.0475);
                gp.rotateY(-HALF_PI);
                return noUV(gp.translate(sx, 0, 0));
            }),
            // colunas B plenas (cintura→beiral) ACOMPANHANDO a casca: meia-largura =
            // shellHalf(y,z) − 0.015 → sempre ~0.015 proud do vidro, sem espetar
            ...[0.585, -0.585].map((px) => clampToShell(tbox(0.09, 0.52, 0.1, px, 1.38, -0.4))),
            tbox(1.26, 0.08, 0.18, 0, 1.16, 0.6), // SOLEIRA do cowl: topo 1.20 ~nivelado com o fim do capô (1.19), senão vira degrau de escada
            // PARA-LAMAS DIANTEIROS: PONTÃO em loft com crista em RAMPA — sobe do bico
            // (0.96) até morrer no cowl (1.21), com o vão da roda recortado embaixo.
            // O toro de raio constante dava arco SIMÉTRICO: a frente lia como parede
            // chata com dois discos colados (erro nº 1 da auditoria da frente).
            ...[0.6, -0.6].map((sx) =>
                zLoft(1.9, 0.72, cylSeg(22), cylSeg(10), (z) => {
                    const dz = z - FA,
                        rOpen = 0.44; // vão do arco = 1.2× o pneu (medido no desenho)
                    return {
                        // bico BAIXO (0.86) subindo até o cowl (1.21) e pontão LARGO
                        // (0.22): estreito, o farol ocupava toda a chapa e o pneu
                        // encostava na borda; a traseira também é larga (0.87)
                        yt: tblAt([[1.9, 0.86], [1.8, 0.93], [1.6, 1.02], [1.32, 1.08], [1.05, 1.15], [0.86, 1.2], [0.72, 1.21]], z),
                        yb: Math.max(0.4, Math.abs(dz) < rOpen ? AY + Math.sqrt(rOpen * rOpen - dz * dz) : 0),
                        a: tblAt([[1.9, 0.13], [1.8, 0.19], [1.6, 0.22], [1.0, 0.22], [0.8, 0.17], [0.72, 0.13]], z),
                        // xc FIXO em 0.6: convergir o pontão no bico (0.5) puxa os
                        // faróis (que ficam em ±WX) para fora da chapa e a frente vira
                        // duas salsichas — testado e revertido
                        xc: sx,
                    };
                })
            ),
            // PARA-LAMAS TRASEIROS: seguem no toro (auditoria da traseira aprovou)
            ...rear.map(([x, z]) => fenderTorus(x, z, 0.45)),
        ]),
        mat: paintMat,
        pos: [0, 0, 0],
        color: "paint",
    });

    // ---- VIDROS: para-brisa quase vertical + laterais ALTAS + janela tras. pequena ----
    {
        // topo dos vidros em ARCO acompanhando o teto (quadrática ~paralela à borda
        // inferior do roofDome, sempre ~0.04 p/ DENTRO dele) → altura de janela quase
        // constante, sem "cunha decrescente". Borda traseira VERTICAL rente à face da
        // cúpula (z-1.14, 0.01 atrás da face -1.13): sela o vão vazado que aparecia
        // entre o topo do vidro e o teto, sem z-fighting; a cúpula pintada é a coluna C.
        // B fica ~0.015-0.02 ATRÁS da face frontal do teto (coplanar dava z-fighting
        // pontilhado na borda do "capuz")
        // borda traseira INCLINADA (paralela à coluna C em rampa, 0.05 atrás dela)
        // A/B RECUADOS 0.025: o para-brisa fica INSET atrás da chapa (montantes +
        // travessa + cowl), como no carro real — antes o vidro era rente e a moldura
        // é que sobressaía, dando o ar de quadro aparafusado
        // para-brisa RECLINADO ~37° (era 18° → janela quadrada de cabine de picape) e
        // vidro lateral esticado até a nova coluna C (cabine +0.12 mais longa)
        // topo do vidro SEMPRE ~0.035 acima do beiral (ROOF_BOT) — embutido na chapa
        const A = [0.6, 1.16],
            B = [0.31, 1.58],
            C = [-1.13, 1.4],
            D = [-1.21, 1.16];
        const s = new THREE.Shape();
        s.moveTo(D[0], D[1]);
        s.lineTo(A[0], A[1]);
        s.lineTo(B[0], B[1]);
        s.quadraticCurveTo(-0.45, 1.74, C[0], C[1]);
        s.closePath();
        // ⚠️ LARGURA FIXA, sem warp. O vidro lateral do Fusca é PLANO; a face lateral
        // desta extrusão é um cap triangulado por EARCUT, então puxar o x dos vértices
        // de cima (que é o que fazia o vidro "acompanhar a casca") DOBRAVA os
        // triângulos e riscava uma diagonal no meio da janela — lia como vidro
        // CHANFRADO. 1.19 é a maior largura que cabe na casca em todo o contorno: o
        // ponto crítico é o canto traseiro (z−1.13, y1.40), onde a cabine já afunilou
        // para 0.603 de meia-largura — 8 mm de folga.
        const gw = 1.19;
        const g = new THREE.ExtrudeGeometry(s, { depth: gw, bevelEnabled: false, curveSegments: curveSeg(12) });
        g.translate(0, 0, -gw / 2);
        g.rotateY(-HALF_PI);
        parts.push({
            // vigia traseira ALTA, flush na cúpula, com cantos BEM arredondados (retângulo duro perdia o cue clássico)
            // vigia GRANDE (~55% da largura do casco), painel CURVO seguindo a cauda
            // (retângulo plano furava a lataria nas pontas), cantos arredondados
            geo: mergeGeometries([
                noUV(g),
                // z −1.19..−1.52 (fora da crista, na rampa) e lift mínimo: na crista o
                // painel vira a silhueta em vistas altas
                // z −1.34..−1.60 (fora do joelho da curva da cauda, onde a seção muda
                // rápido e o painel projetava como LOSANGO) e contorno mais retangular
                // vigia TRAPEZOIDAL de cantos arredondados: mais larga na borda de cima
                // (0.31, junto ao teto) estreitando p/ baixo (0.25), acompanhando o
                // afunilamento da cauda — a pílula de largura constante não lia como
                // vigia de Fusca. Cantos com expoente 8 (arredondados, não bicudos).
                shellPatch(
                    -1.4, // começa onde a cauda já está inclinada ~50°: mais à frente
                    -1.64, // o topo cai no trecho horizontal e a perspectiva o afina em bico
                    // taper suave (0.33→0.28) e cantos LIMITADOS a 55% da largura —
                    // sem o piso, as pontas fecham em zero e a vigia vira escudo
                    (u) => (0.33 - 0.05 * u) * Math.max(0.55, Math.pow(Math.max(0, 1 - Math.pow(Math.abs(2 * u - 1), 12)), 1 / 12)),
                    0.0025,
                    curveSeg(10),
                    curveSeg(12)
                ),
            ]),
            mat: glass,
            pos: [0, 0, 0],
        });
    }

    // ---- ESCURO (clad): estribos + grelhas do motor + wells ----
    parts.push({
        geo: mergeGeometries([
            tbox(0.16, 0.055, 1.5, 0.735, 0.3825, -0.1), // estribo: topo 0.41 ENCOSTA na soleira do corpo (0.36), sem vão, e mais longo
            tbox(0.16, 0.055, 1.5, -0.735, 0.3825, -0.1), // estribo E
            // (as linhas de corte da porta saíram daqui — viraram peça própria no TOM
            // DA PINTURA, ver `doorCuts` abaixo: pretas liam como adesivo)
            // GRELHA do motor: banda de aletas VERTICAIS logo abaixo do vidro (como no
            // real; as 2 barras horizontais antigas eram orientação trocada)
            // venezianas CURTAS (~28% da altura da vigia) na largura dela, logo abaixo
            // (faixa alta demais lia como grade grande)
            // venezianas: painéis CURVOS colados na cauda (caixa reta sobre superfície
            // convexa levanta as pontas ~0.02 e rompe a silhueta lateral)
            ...[-0.24, -0.18, -0.12, -0.06, 0, 0.06, 0.12, 0.18, 0.24].map((gx) =>
                // venezianas afastadas: faixa de CHAPA de ~0.08 (7 cm reais) entre a
                // base da vigia (−1.62) e o topo das aletas (−1.70), como no carro real
                shellPatch(-1.72, -1.84, () => 0.016, 0.004, curveSeg(4), 1, gx)
            ),
            ...slots.map(([x, z]) => axleDisc(0.35, 0.1, 18, x * 0.85, AY, z)), // wells escuros
        ]),
        mat: clad,
        pos: [0, 0, 0],
    });

    // ---- SULCOS DAS PORTAS: TOM DA PINTURA, 15% mais escuro ----
    // No carro real a fresta da porta é SOMBRA da própria chapa, não uma peça preta:
    // em preto ela lia como adesivo colado no flanco (dois riscos pretos num carro
    // claro). 'paintShade' deriva do tom do jogador, então acompanha a cor escolhida.
    parts.push({
        geo: mergeGeometries(
            // dianteira sob o PÉ DA COLUNA A / canto do para-brisa (z 0.575) e traseira
            // na COLUNA B (z −0.40), que é onde o vidro da porta acaba.
            [0.575, -0.4].flatMap((dz) =>
                [1, -1].map((sg) => {
                    // a linha SEGUE a superfície do flanco com folga CONSTANTE de 0.008.
                    // Reta, no terço alto a folga caía a ~1 mm, virava coplanar e a
                    // listra SUMIA num pedaço (z-fighting) — "engolida pela lataria".
                    const g = tbox(0.03, 0.64, 0.028, 0, 0.76, dz); // termina em y1.08, antes da curvatura forte
                    const p = g.attributes.position;
                    for (let i = 0; i < p.count; i++) {
                        const y = p.getY(i);
                        // curva MEDIDA na malha do tub (fillet 0.2 + tumblehome): reta
                        // até y1.0 e recolhendo depois. A fórmula analítica do fillet
                        // subestimava em 8 cm e enterrava a listra.
                        const surf = y <= 1.0 ? 0.717 : 0.717 - (y - 1.0) * 0.407;
                        p.setX(i, sg * (surf + 0.008) + p.getX(i));
                    }
                    g.computeVertexNormals();
                    return g;
                })
            )
        ),
        mat: paintMat,
        pos: [0, 0, 0],
        color: "paintShade",
    });

    // ---- CROMADOS: para-choques de lâmina, aros dos faróis, frisos, maçaneta, VW ----
    parts.push({
        geo: mergeGeometries([
            // para-choques mais GROSSOS, com overriders e SUPORTES tocando o avental
            // (lâminas finas flutuando longe liam como barras de Ford T)
            tbox(1.42, 0.1, 0.09, 0, 0.55, 2.02), // para-choque diant.: LÂMINA fina (0.06 da altura do carro, medido na foto) e mais estreita que os para-lamas
            tbox(1.56, 0.16, 0.16, 0, 0.55, -2.0), // para-choque traseiro (folga da cauda ovalada)
            tbox(0.06, 0.18, 0.09, 0.36, 0.56, 2.05), // overrider diant. D
            tbox(0.06, 0.18, 0.09, -0.36, 0.56, 2.05), // overrider diant. E
            tbox(0.06, 0.2, 0.1, 0.36, 0.56, -2.03), // overrider tras. D
            tbox(0.06, 0.2, 0.1, -0.36, 0.56, -2.03), // overrider tras. E
            tbox(0.05, 0.05, 0.34, 0.36, 0.55, 1.9), // suporte diant. D (liga avental→lâmina)
            tbox(0.05, 0.05, 0.34, -0.36, 0.55, 1.9), // suporte diant. E
            tbox(0.05, 0.05, 0.24, 0.36, 0.55, -1.9), // suporte tras. D (rabeta→lâmina)
            tbox(0.05, 0.05, 0.24, -0.36, 0.55, -1.9), // suporte tras. E
            // aros dos faróis quase VERTICAIS (deitados ~37° liam como "olhos
            // sonolentos"; nas fotos a lente olha p/ FRENTE), na face do para-lama à
            // meia-altura do capô, face traseira ancorada na chapa
            // aros: farol BAIXO (centro y0.80 = 40% da altura, medido no desenho) e no
            // bico do para-lama, com a crista da chapa fazendo SOBRANCELHA por cima
            // ⚠️ aro FUNDO (0.18) e não disco raso: a tampa dianteira do para-lama fica
            // em z1.90, e com 0.07 de profundidade a face do aro sobrava só 0.003 além
            // dela — a chapa PINTADA cortava o conjunto e aparecia como uma fresta da
            // cor do carro entre a lente e o aro. Com 0.18 a face do aro fica 0.02
            // PROUD da chapa e a lente 0.013 proud do aro: entre lente e lataria só
            // existe cromado. Coaxial com a lente (mesmo eixo, 17° p/ cima).
            cyl(0.125, 0.18, 24).rotateX(HALF_PI - 0.3).translate(-WX, 0.753, 1.834),
            cyl(0.125, 0.18, 24).rotateX(HALF_PI - 0.3).translate(WX, 0.753, 1.834),
            // maçaneta e emblema sobem junto com a crista do capô (senão afundam nela)
            box(0.04, 0.025, 0.14).rotateX(0.67).translate(0, 0.948, 1.78), // maçaneta CHATA rente ao capô (em pé lia como mascote de radiador)
            cyl(0.055, 0.025, 12).rotateX(HALF_PI - 0.67).translate(0, 1.008, 1.69), // emblema VW CHATO deitado na coroa
        ]),
        mat: chrome,
        pos: [0, 0, 0],
    });

    // ---- FARÓIS REDONDOS em pé nos para-lamas (bug eyes) ----
    parts.push({
        geo: mergeGeometries([
            cyl(0.1, 0.07, 24).rotateX(HALF_PI - 0.3).translate(-WX, 0.773, 1.9),
            cyl(0.1, 0.07, 24).rotateX(HALF_PI - 0.3).translate(WX, 0.773, 1.9),
        ]),
        mat: headMat,
        pos: [0, 0, 0],
    });

    // ---- LANTERNAS ovais na rampa traseira dos para-lamas ----
    parts.push({
        geo: mergeGeometries([
            // lanternas OVAIS na face TRASEIRA do para-lama: ponto φ=30° do toro
            // (contado do bico traseiro), eixo ao longo da NORMAL dali → miram p/ trás
            // e um pouco p/ cima, acima do para-choque, como na foto. O raio importa:
            // centro a 0.60 do eixo do toro (superfície em 0.63) → metade do tambor
            // ancorada na chapa e metade PROUD; a 0.55 some dentro do para-lama.
            cyl(0.085, 0.14, 12).rotateX(-1.047).translate(-0.62, 0.658, -1.827),
            cyl(0.085, 0.14, 12).rotateX(-1.047).translate(0.62, 0.658, -1.827),
        ]),
        mat: tailMat,
        pos: [0, 0, 0],
    });

    // ---- PISCAS âmbar no topo dos para-lamas dianteiros ----
    parts.push({
        // piscas: BALA DEITADA na crista do para-lama, entre o farol e o cowl (cilindro
        // em pé no flanco do capô lia como lasca colada)
        geo: mergeGeometries([
            cyl(0.032, 0.1, 10).rotateX(HALF_PI).translate(-0.6, 1.03, 1.52),
            cyl(0.032, 0.1, 10).rotateX(HALF_PI).translate(0.6, 1.03, 1.52),
        ]),
        mat: amber,
        pos: [0, 0, 0],
    });

    // ---- RODAS: pneu fino + roda de AÇO + CALOTA cromada; dianteiras esterçam ----
    // pneu 0.365 (Ø 0.42 da altura) · aro de aço 0.65 do Ø · CALOTA pequena (0.24 do
    // Ø, medida na foto) deixando anel de aro à mostra — calota grande lia como taça
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => wheelDisc(0.365, 0.3, 18, x, z))), tire, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => axleDisc(0.237, 0.33, 14, x, AY, z))), rimMat, AY, RA));
    parts.push(rearAxlePart(mergeGeometries(rear.map(([x, z]) => axleDisc(0.088, 0.36, 10, x, AY, z))), chrome, AY, RA));
    for (const [x, z] of front) parts.push(...steerWheelParts(x, z, AY, [
        { r: 0.365, depth: 0.3, seg: 18, mat: tire },
        { r: 0.237, depth: 0.33, seg: 14, mat: rimMat },
        { r: 0.088, depth: 0.36, seg: 10, mat: chrome },
    ]));

    return parts;
}
